import { AxiosError } from 'axios'
import {
  useQuery as useQueryHook,
  UseQueryOptions as UseQueryBaseOptions,
  QueryKey,
} from '@tanstack/react-query'
import { useAuthentication } from '@/components/auth/AuthProvider'
import { useEffect, useRef } from 'react'

type UseQueryOptions<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> = UseQueryBaseOptions<TQueryFnData, TError, TData, TQueryKey>

export default function useQuery<
  TQueryFnData = unknown,
  TError = unknown,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>({ ...options }: UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>) {
  const { getFreshIdToken } = useAuthentication()
  const hasRefreshed = useRef(false) // Used to prevent multiple refresh attempts

  const query = useQueryHook<TQueryFnData, TError, TData, TQueryKey>({
    ...options,
  })

  useEffect(() => {
    if (
      !hasRefreshed.current &&
      (query.error as AxiosError)?.response?.status === 401
    ) {
      hasRefreshed.current = true
      getFreshIdToken()?.catch(() => {})
    }
  }, [getFreshIdToken, query.error])

  return query
}
